import { getPaneElements } from '../../../util/chartPanes'
import { debounce } from 'throttle-debounce'
import tinycolor from 'tinycolor2'
import { callNative } from '../../../util/nativeBridge'
import cloneDeep from 'lodash/cloneDeep'
import isEqual from 'lodash/isEqual'
import { ColorType, CrosshairMode, DeepPartial, LineStyle, LineWidth, PriceScaleMode, SingleValueData, Time, TimeChartOptions, TrackingModeExitMode } from 'lightweight-charts'
import React, { forwardRef, memo, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { ACCESS_TOKEN, CHART_PRICE_SCALE_MARGINS, chartLayoutOptions, ChartViewKey } from '../../../assets/constants'
import { canvasLineStyleToLineStyle, mergeCanvasSettings } from '../../../util/canvasSettingsDefaults'
import { useDispatch, useSelector } from 'react-redux'
import { Box, Paper, useTheme } from '@mui/material'
import { ChartContext } from '../chart.context'
import { ChartCandle, ChartConfig, ChartMode, PaneConfig, Scale, Series, SeriesGroup } from '../chart.typings'
import { AppContext } from '../../../App'
import {
    selectActiveChartId,
    selectChartConfig,
    selectChartDrawings,
    selectChartPanes,
    selectChartPanesConfig,
    selectChartReplayMode,
    selectChartSeries,
    selectChartSeriesGroups,
    selectChartTimeBucket,
    selectChartType,
    selectChartStoreIndicators,
    selectChartStoreTimeBuckets,
    selectExchangeTradingSessions,
    selectIndicators,
    selectOverviewSidebarChartConfig,
    selectOverviewSidebarChartPanes,
    selectOverviewSidebarChartPanesConfig,
    selectOverviewSidebarChartSeries,
    selectOverviewSidebarChartSeriesGroups,
    selectOverviewSidebarChartTimeBucket,
    selectOverviewSidebarChartType,
    selectScreenerChartType,
    selectScreenerSelectedDateRange,
    selectTimeBuckets,
    selectTradingCalendarDataByExchange
} from '../../../selectors/chart'
import { AlertData, AlertSeriesGrp } from '../../alerts/alertMenuItem/AlertMenuItem'
import Chart from '../chart'
import { selectCurrentTheme, selectTimezone } from '../../../selectors/common'
import { convertExpressionToDisplayExpression } from '../../../util/expression'
import { SeriesGroupCategory } from '../../../pages/home'
import {
    disableReplayMode,
    ReplayStatus,
    setActiveChart,
    updateChartConfig,
    updateChartDrawings,
    updateChartTimeBucket,
    updateChartType,
    updatePanesConfig
} from '../../../reducer/chart.reducer'
import { areTwoRangesAlmostSame, CrossHairInfo, defaultTradingSettings, getDefaultChartConfig, getDefaultChartPanesConfig, getEffectiveAlertSettings, getNewPanesConfig, ViewportCandleInfo } from '../../../util/chart'
import { WebSocketContext } from '../../webSocketConnection/WebSocket'
import { Security, SecurityCategory_CategoryCode } from '../../../proto/generated/security'
import websocketActions from '../../../actions/websocket.actions'
import { ChartMetadata } from '../chartMetadata/chartMetadata'
import { WebSocketRequestMessage_Security, WebSocketRequestMessage_Subscription } from '../../../proto/generated/websocket'
import { Exchange, Params, TimeBucket, TimeBucket_TimeSeries } from '../../../proto/generated/common'
import ScrollToRecentBarButton from '../scrollToRecentBarButton/ScrollToRecentBarButton'
import ChartContextMenuHost, { ChartContextMenuHostHandle } from '../chartContextMenuOptions/ChartContextMenuHost'
import store from '../../../store'
import ChartScaleMenu from '../chartScale/chartScaleMenu'
import { DrawingDisplayType, DrawingEvents, DrawingTypes } from '../drawingTools/drawing.interface'
import useSecurityExpressionToName from '../../../hooks/useExpressionNames'
import ChartSettingsButtons from '../chartContextMenuButton/ChartSettingsButtons'
import { binarySearch } from '../../../util/general'
import { getSecurityKey, resolveSecuritiesFromKeys } from '../../../util/security'
import { fetchSecuritySnapshotsBulk } from '../../../services/apis/candles'
import { ChartAlertSnapshot } from '../../alerts/alertModal.typings'
import IndicatorSeriesGroup from '../indicatorSeriesGroup'
import { convertToTimeBucket } from '../../../util/timeBuckets'
import { getIndicatorDisplayName } from '../../../util/indicators'
import ChartOverlays from '../ChartOverlays'
import { setChartSnapshotForView, setChartCrosshairSnapshotForView } from '../chartSnapshotStore'
import { bumpChartCrosshairRevision } from '../chartCrosshairRevisionStore'
import { publishChartBidAsk } from '../chartBidAskStore'
import { setChartHoverActive } from '../chartHoverState'

import { useUndoRedoContext } from '../../UndoRedoFunctonalities/UndoRedo.context'
import UndoRedoManager from '../../../UndoRedoManager/UndoRedoManager'
import { selectLayoutWidgets, selectSyncSettings } from '../../../selectors/widgetDashboard'
import { CompanyEventProto } from '../../../proto/generated/company_event'
import { companyEventsFetcher } from '../../../selectors/companyEvents'

import useStyles from './styles'
import ChartFooter from '../chartFooter/chartFooter'
import ChartSkeletonPlaceholder from '../../common/ChartSkeletonPlaceholder'
import ChartMetadataBarPlaceholder from '../../common/ChartMetadataBarPlaceholder'
import ScissorIcon from '../../../assets/icons/ScissorIcon'
import { selectOrdersHistory } from '../../../selectors/orders'
import { Order, OrderStatus, OrderType, Product } from '../../../proto/generated/order'
import { Position, Position_Status } from '../../../proto/generated/position'
import { utcToIST } from '../../../util/dateTime'
import { TradeOptions } from '../../../reducer/scalper.reducer'
import { selectIsEmbedded, selectIsTouchEnabled } from '../../../selectors/device'
import { ChartType } from '../../../proto/generated/chart'
import { SuperChartConfiguration_TimeBucket, SuperChartConfiguration_DateRange } from '../../../proto/generated/superchart-page'
import { useUser } from '../../../context/UserContext.tsx'
import { FeatureCategory, FeatureSubCategory, getTimeBucketFeatureName } from '../../adminUserRestrictions/Restrictions.constants.ts'
import ChartRestrictionOverlay from '../chartRestrictionOverlay/ChartRestrictionOverlay.tsx'
import ChartErrorOverlay from '../chartErrorOverlay/ChartErrorOverlay'
import ChartEmptyStateOverlay from '../chartEmptyStateOverlay/ChartEmptyStateOverlay'
import { selectPositions } from '../../../selectors/positions'
import { selectSnapshot } from '../../../selectors/security'
import { selectUserAlerts } from '../../../selectors/alerts'
import { filterAlertsForChartPriceLines } from '../../../util/alerts'
import { isPatternIndicator } from '../../../util/patternIndicators'
import { selectPrimaryTradingAccount, selectUserDetails } from '../../../selectors/user'
import { BrokerId } from '../../../util/tradingAccounts.ts'
import { calculateGcd } from '../../../util/orders.ts'
import { isBasketStrategyBasketId } from '../../../util/positions'
import { useParams } from 'react-router-dom'
import { selectBasketId, selectStrategyBuilderTab, selectTradeOrders } from '../../../selectors/strategyBuilder.ts'
import { StrategyBuilderTab } from '../../strategyBuilder/header/index.tsx'
import { TransactionType } from '../../../proto/generated/order.ts'
import { useExchangeLiveTradingWindow } from '../../../hooks/useExchangeLiveTradingWindow'
import ChartMountSurface from './ChartMountSurface'
import ChartMountOverlays, { ChartMountOverlaysProps } from './ChartMountOverlays'
import InlineCrosshairOverlay from './InlineCrosshairOverlay'
import TouchDrawStepBanner from './TouchDrawStepBanner'
import { useChartOrderDrawingSync } from './useChartOrderDrawingSync'
import IndicatorsDrawingsShortcutsRegister from '../../KeyboardShortcuts/IndicatorsDrawingsShortcutsRegister.tsx'

export type ChartWrapperProps = {
    viewKey: string
    mode: ChartMode
    primary: boolean
    securityList: string[]
    comparableSecurities: Security[]
    expression?: string,
    expressionName?: string,
    showMetadata?: boolean
    persistViewport?: boolean
    disableRealtimeUpdates?: boolean
    historyManager?: UndoRedoManager<any, any>
    hideChartFooter?: boolean
    hideActions?: boolean
    toggleFocus?: boolean
    allowSplit?: boolean,
    tradeOptions?: TradeOptions,
    primaryScaleId?: Scale,
    hideTimescale?: boolean,
    hideLeftScale?: boolean,
    hideRightScale?: boolean,
    disableDrawings?: boolean,
    disableVolumeOnLoad?: boolean,
    noWhitespace?: boolean,
    noOffset?: boolean,
    selectedDateRange?: SuperChartConfiguration_DateRange,
    /** When set (e.g. embedded URL), use this interval instead of the value stored for viewKey */
    timeBucketOverride?: SuperChartConfiguration_TimeBucket,
    onSecuritySelected?: (key: string, expressionMode: boolean, securities: string[]) => void
    /** `undefined` means the crosshair was cleared (mouse left / touch tracking exited) so widgets can broadcast a clear. */
    onCrosshairMove?: (data?: CrossHairInfo) => void
    onTimeRangeChange?: (viewportCandleInfo: ViewportCandleInfo, gotoDate: number | undefined) => void
    onViewportCandleInfoChange?: (viewportCandleInfo: ViewportCandleInfo) => void
    onDrawingEvent?: (event: DrawingEvents, data: any, forced?: boolean) => void

    screenerChart?: boolean
    disableScroll?: boolean
    /** Native embedded overview: long-press vertical crosshair with inline OHLC card and two-finger range (see InlineCrosshairOverlay). */
    inlineCrosshair?: boolean
    initialConfig?: Partial<ChartConfig>
    isWidget?: boolean
    /** When provided (e.g. from ChartWidget), skip re-resolving securities on mount. */
    resolvedSecurities?: Security[]
}

export type ChartContextType = {
    hoverActive: boolean
    hoveredPaneId: number | undefined
    chart: Chart
    viewKey: string
    seriesGroupErrors: Record<string, string>
}

/** Overview chart ignores volatile store slices (positions, orders, etc.) so ticks do not re-render the shell. */
const ignoreSelectorUpdates = () => true

const ChartWrapper = forwardRef<any, ChartWrapperProps>((props, ref) => {
    const dispatch = useDispatch()
    const {
        viewKey,
        securityList,
        expression,
        expressionName,
        mode,
        comparableSecurities,
        disableRealtimeUpdates,
        showMetadata = true,
        hideChartFooter,
        onSecuritySelected,
        primary,
        hideActions = false,
        allowSplit = true,
        primaryScaleId = "RIGHT",
        persistViewport = true,
        toggleFocus = false,
        tradeOptions,
        onCrosshairMove,
        onTimeRangeChange,
        onViewportCandleInfoChange,
        onDrawingEvent,
        screenerChart = false,
        disableScroll = false,
        inlineCrosshair = false,
        initialConfig,
        isWidget = true,
        hideTimescale = false,
        hideLeftScale,
        hideRightScale = false,
        disableVolumeOnLoad = false,
        noWhitespace = false,
        noOffset = false,
        disableDrawings,
        selectedDateRange,
        timeBucketOverride,
        resolvedSecurities: preResolvedSecurities
    } = props
    const isOverviewChart = viewKey === ChartViewKey.OVERVIEW
    /** Overview sidebar chart: crosshair/mouse must not touch React state (pane hover flicker re-renders every move). */
    const freezeChartHoverReactState = isOverviewChart
    const freezeStoreSelector = isOverviewChart ? ignoreSelectorUpdates : undefined
    const { classes, cx } = useStyles()
    const ws = useContext(WebSocketContext)
    const [container, setContainer] = useState<HTMLDivElement>()
    const handleChartMount = useCallback((element?: HTMLDivElement) => {
        setContainer(element)
    }, [])
    const chartErrorRef = useRef<string | null>(null)
    const chartEmptyRef = useRef(false)
    const params = useParams()
    const selectedTab = useSelector(selectStrategyBuilderTab, freezeStoreSelector)
    const selectedBasketId = useSelector(selectBasketId, freezeStoreSelector)
    const basketId: string = useMemo(() => {
        return params.basketId || (selectedTab == StrategyBuilderTab.StrategyChart ? selectedBasketId : '')
    },[selectedBasketId, selectedTab, params.basketId])
    const [chartContext, setChartContext] = useState<ChartContextType | null>(null)
    const showLogo = useMemo(() => {
        return screenerChart ? true : false
    }, [screenerChart])

    const chartContextRef = useRef(chartContext)
    const [layoutId] = useMemo(() => viewKey.split('-'), [viewKey])
    const isEmbeded = useSelector(selectIsEmbedded, freezeStoreSelector)
    const widgets = useSelector(selectLayoutWidgets(layoutId), freezeStoreSelector)
    const syncSettings = useSelector(selectSyncSettings(layoutId), freezeStoreSelector)
    const chartPanes = useSelector(isOverviewChart ? selectOverviewSidebarChartPanes : selectChartPanes(viewKey))
    const chartPanesConfig = useSelector(isOverviewChart ? selectOverviewSidebarChartPanesConfig : selectChartPanesConfig(viewKey))
    const chartSeriesGroups = useSelector(isOverviewChart ? selectOverviewSidebarChartSeriesGroups : selectChartSeriesGroups(viewKey))
    const chartSeries = useSelector(isOverviewChart ? selectOverviewSidebarChartSeries : selectChartSeries(viewKey))
    const chartType = useSelector(
        screenerChart ? selectScreenerChartType : isOverviewChart ? selectOverviewSidebarChartType : selectChartType(viewKey)
    )
    const selectedDateRangeScreener = useSelector(selectScreenerSelectedDateRange, freezeStoreSelector)
    const chartConfig = useSelector(isOverviewChart ? selectOverviewSidebarChartConfig : selectChartConfig(viewKey))
    const storeTimeBucket = useSelector(isOverviewChart ? selectOverviewSidebarChartTimeBucket : selectChartTimeBucket(viewKey))
    const timeBuckets = useSelector(isOverviewChart ? selectChartStoreTimeBuckets : selectTimeBuckets)
    const timeBucket = useMemo(() => {
        if (screenerChart) {
            return timeBuckets.filter((v) => v.code === selectedDateRangeScreener?.timeBucketCode)[0] ?? storeTimeBucket
        }
        return timeBucketOverride ?? storeTimeBucket
    }, [screenerChart, timeBuckets, selectedDateRangeScreener?.timeBucketCode, storeTimeBucket, timeBucketOverride])

    /** Embedded URL can fix timeBucket (e.g. ONE_DAY) while preset date ranges still carry another timeBucketCode (e.g. ONE_WEEK for 6M). Align so setSelectedDateRange + fetch match the override and avoid fighting updateChartTimeBucket. */
    const dateRangeForChart = useMemo((): SuperChartConfiguration_DateRange | undefined => {
        if (!selectedDateRange || screenerChart) return selectedDateRange
        if (!timeBucketOverride?.code || selectedDateRange.timeBucketCode === timeBucketOverride.code) {
            return selectedDateRange
        }
        return { ...selectedDateRange, timeBucketCode: timeBucketOverride.code }
    }, [screenerChart, selectedDateRange, timeBucketOverride])

    const timezone = useSelector(selectTimezone)
    const indicators = useSelector(isOverviewChart ? selectChartStoreIndicators : selectIndicators)
    const chart = useRef<Chart>(null)
    const chartConfigRef = useRef(chartConfig) // keep a reference of Chart config within the library for comparsion with state config.
    const mounted = useRef(false)
    const [paneContainers, setPaneContainers] = useState<HTMLDivElement[]>([])
    const panesConfig = useSelector(isOverviewChart ? selectOverviewSidebarChartPanesConfig : selectChartPanesConfig(viewKey))
    const subscriptionTag = useMemo(() => `${viewKey}-chart`, [viewKey])
    const { manager } = useUndoRedoContext()
    const [loading, setLoading] = useState(false)
    const [chartError, setChartError] = useState<string | null>(null)
    const [chartEmpty, setChartEmpty] = useState(false)
    const [gotoCurrentButtonVisible, setGotoCurrentButtonVisible] = useState(false)
    const [showRestrictionOverlay, setShowRestrictionOverlay] = useState(false)
    const { user: { hasAccess } } = useUser()
    const contextMenuHostRef = useRef<ChartContextMenuHostHandle>(null)
    const activeChartId = useSelector(selectActiveChartId, freezeStoreSelector)
    const appContext = useContext(AppContext)
    const futureCompanyEvents = useSelector(companyEventsFetcher, freezeStoreSelector) as Record<string, CompanyEventProto[]>
    const expressionToName = useSecurityExpressionToName()
    const chartRef = useRef<HTMLDivElement>(null)
    const chartActive = useMemo(() => viewKey === activeChartId, [viewKey, activeChartId])
    // In multi-pane layouts only the focused (or hovered) chart shows its settings button.
    const isMultiPaneLayout = isWidget && Object.keys(widgets ?? {}).length > 1
    const chartActiveRef = useRef(chartActive)
    const [gotoDate, setGotoDate] = useState<number>()
    const previousViewPortCandleInfo = useRef<ViewportCandleInfo | null>(null)
    const [chartInitialized, setChartInitialized] = useState(false)
    const [hasCompletedInitialLoad, setHasCompletedInitialLoad] = useState(false)
    const [configUpdateVersion, setConfigUpdateVersion] = useState(0)
    const [resolvedSecurities, setResolvedSecurities] = useState<Security[]>([])
    const resolvedSecuritiesMapRef = useRef<Map<string, Security>>(new Map())
    const primarySecurityKey = resolvedSecurities[0] ? getSecurityKey(resolvedSecurities[0].exchange, resolvedSecurities[0].code) : ''
    const isIndividualOptionChart = useMemo(
        () =>
            mode !== 'EXPRESSION' &&
            resolvedSecurities[0]?.categoryCode === SecurityCategory_CategoryCode.OPTIONS,
        [mode, resolvedSecurities]
    )
    const darkTheme = useSelector(selectCurrentTheme)
    const theme = useTheme()
    const replayState = useSelector(selectChartReplayMode, freezeStoreSelector)
    const positions = useSelector(selectPositions, freezeStoreSelector)
    const ordersHistory = useSelector(selectOrdersHistory, freezeStoreSelector)
    const userAlerts = useSelector(selectUserAlerts, freezeStoreSelector)
    const isTouchEnabled = useSelector(selectIsTouchEnabled, freezeStoreSelector)
    const tradeOrders = useSelector(selectTradeOrders, freezeStoreSelector)

    const isReplayModeOn = useMemo(() => !!replayState, [replayState])
    const exchangeTradingSessions = useSelector(selectExchangeTradingSessions, freezeStoreSelector)
    const isLiveTradingWindow = useExchangeLiveTradingWindow(exchangeTradingSessions, resolvedSecurities[0])
    const calendarData = useSelector(selectTradingCalendarDataByExchange(resolvedSecurities[0]?.exchange ?? chart.current?.securities[0]?.exchange ?? Exchange.NSE))

    const gotoDateRef = useRef<number>(undefined)
    const viewportCandleInfoRef = useRef<ViewportCandleInfo | null>(null)
    const prevViewportCandleInfoRef = useRef<ViewportCandleInfo | null>(null)
    const [showChartTooltip, setShowChartTooltip] = useState(false)
    const [tooltipTitle, setTooltipTitle] = useState('false')
    const tooltipData = useRef({
        x: 0,
        y: 0
    })

    const basketPositions = useMemo(() => {
        if(!basketId) return []
        return positions.filter(p => p.basketId === basketId)
    }, [positions, basketId])
    
    const indicatorsDrawingsShortcuts = <IndicatorsDrawingsShortcutsRegister viewKey={viewKey} />
    
    const [screenerTooltipVisible, setScreenerTooltipVisible] = useState(false)
    const [screenerTooltipPosition, setScreenerTooltipPosition] = useState({ x: 0, y: 0 })
    const [screenerTooltipData, setScreenerTooltipData] = useState<{ price?: number; volume?: number; isProfit?: boolean; time?: number } | null>(null)

    useEffect(() => {
        if (!securityList?.length) {
            setResolvedSecurities([])
            resolvedSecuritiesMapRef.current.clear()
            return
        }

        if (
            preResolvedSecurities?.length === securityList.length &&
            securityList.every((key, i) => {
                const sec = preResolvedSecurities[i]
                return sec && getSecurityKey(sec.exchange, sec.code) === key
            })
        ) {
            setResolvedSecurities(preResolvedSecurities)
            const map = new Map<string, Security>()
            preResolvedSecurities.forEach((sec) => map.set(getSecurityKey(sec.exchange, sec.code), sec))
            resolvedSecuritiesMapRef.current = map
            return
        }

        let cancelled = false
        resolveSecuritiesFromKeys(securityList, mode === 'EXPRESSION').then(({ securities, map }) => {
            if (!cancelled) {
                setResolvedSecurities(securities)
                resolvedSecuritiesMapRef.current = map
            }
        })
        return () => { cancelled = true }
    }, [securityList, mode, preResolvedSecurities])

    const chartOrders = useMemo(() => {
        let result: Record<number, Order[]> = {}
        const security = securityList?.[0]
        if (mode !== 'EXPRESSION' && security) {
            const filtered = ordersHistory?.filter(o => {
                const key = getSecurityKey(o.security?.exchange ?? 0, o.security?.code ?? '')
                if (key !== security || o.status !== OrderStatus.EXECUTED) return false
                if (isIndividualOptionChart && isBasketStrategyBasketId(o.basketId)) return false
                return true
            })
            filtered?.forEach(d => {
                const time = utcToIST((d?.exchangeTimestamp ?? 0) / 1000)
                if (result[time]) {
                    result[time].push(d)
                } else {
                    result[time] = [d]
                }
            })
        }
        return result
    }, [mode, ordersHistory, securityList, isIndividualOptionChart])

    const chartAlertsOverlay = useMemo(
        () => {
            const { onlyActiveAlerts } = getEffectiveAlertSettings(chartConfig)
            return filterAlertsForChartPriceLines(
                userAlerts,
                resolvedSecurities[0],
                mode === 'EXPRESSION',
                comparableSecurities ?? [],
                expression ?? '',
                onlyActiveAlerts
            )
        },
        [userAlerts, resolvedSecurities, mode, comparableSecurities, expression, chartConfig?.alertSettings, chartConfig?.tradingSettings]
    )

    const createBasketSLTPOrders = useMemo(() => {
        const result: Order[] = []
        const slOrder = ordersHistory?.find(o =>
            o.orderType === OrderType.STOP_LOSS &&
            o.basketId === basketId &&
            (o.status === OrderStatus.OPEN || o.status === OrderStatus.PENDING || o.status === OrderStatus.TRIGGER_PENDING || o.status === OrderStatus.INACTIVE)
        ) ?? null
        const tpOrder = ordersHistory.find(o =>
            o.orderType === OrderType.TARGET &&
            o.basketId === basketId &&
            (o.status === OrderStatus.OPEN || o.status === OrderStatus.PENDING || o.status === OrderStatus.TRIGGER_PENDING || o.status === OrderStatus.INACTIVE)
        ) ?? null

        const basketTradeOrders = (tradeOrders || []).filter(order => order.selected)

        if (!basketPositions.length) return result

        // Build security to order map (same logic as getAnalyzedGroupedPositions)
        const securityToOrderMap: Record<string, typeof basketTradeOrders> = {};
        basketTradeOrders.forEach(order => {
            if (!securityToOrderMap[order.security.code]) {
                securityToOrderMap[order.security.code] = [];
            }
            securityToOrderMap[order.security.code].push(order);
        });

        // Track which securities have been processed
        const processedSecurities = new Set<string>();

        // Calculate collective position value including orders
        let closedValue = 0
        let quantities: number[] = []

        // Process existing positions with their matching orders
        basketPositions.forEach(pos => {
            processedSecurities.add(pos.security.code);
            const orders = securityToOrderMap[pos.security.code] || [];

            let positionValue = pos.closePositionValue
            let positionQuantity = pos.closePosition

            // If there's exactly one order for this security, process it
            if (orders.length === 1) {
                const order = orders[0];
                const direction = order.transactionType === TransactionType.BUY ? 1 : -1;

                if (Math.sign(positionQuantity) !== Math.sign(direction)) {
                    // Squaring off position
                    const orderSqOffQuantity = Math.min(Math.abs(positionQuantity), Math.abs(order.quantity));
                    const newQuantity = positionQuantity + direction * order.quantity;

                    if (orderSqOffQuantity < order.quantity) {
                        // Completely squared off and reversed
                        positionValue = (order.price ?? 0) * newQuantity
                        positionQuantity = newQuantity
                    } else {
                        // Partially or fully squared off
                        positionValue = (pos.closePositionValue / pos.closePosition) * newQuantity
                        positionQuantity = newQuantity
                    }
                } else {
                    // Adding to position
                    const orderValue = (order.price ?? 0) * order.quantity * direction
                    positionValue += orderValue
                    positionQuantity += order.quantity * direction
                }
            }

            // Add to collective totals
            closedValue += positionValue
            if (positionQuantity !== 0) {
                quantities.push(Math.abs(positionQuantity))
            }
        })

        // Process new orders (for securities without existing positions)
        Object.keys(securityToOrderMap).forEach(securityCode => {
            if (!processedSecurities.has(securityCode)) {
                const orders = securityToOrderMap[securityCode];
                orders.forEach(order => {
                    const direction = order.transactionType === TransactionType.BUY ? 1 : -1;
                    const orderValue = (order.price ?? 0) * order.quantity * direction
                    const orderQuantity = order.quantity * direction

                    closedValue += orderValue
                    quantities.push(Math.abs(orderQuantity))
                })
            }
        })

        const closeQuantity = quantities.length > 0 ? calculateGcd(quantities) : 1
        const entry = closedValue/closeQuantity

        if (slOrder) {
            const slLtp = entry - slOrder.triggerPrice/closeQuantity
            result.push({
                ...slOrder,
                triggerPrice: slLtp,
            })
        }

        if (tpOrder) {
            const targetLtp = entry + tpOrder.price/closeQuantity
            result.push({
                ...tpOrder,
                price: targetLtp,
            })
        }
        return result
    }, [basketId, basketPositions, ordersHistory, tradeOrders])

    const openOrders = useMemo(() => {
        let result: Order[] = []
        const security = securityList?.[0]
        if (mode !== 'EXPRESSION' && security) {
            result = ordersHistory?.filter(o => {
                const key = getSecurityKey(o.security?.exchange ?? 0, o.security?.code ?? '')
                if (key !== security) return false
                const isOpenOrderStatus =
                    o.status === OrderStatus.OPEN ||
                    o.status === OrderStatus.PENDING ||
                    o.status === OrderStatus.TRIGGER_PENDING ||
                    o.status === OrderStatus.INACTIVE
                if (!isOpenOrderStatus) return false
                if (isIndividualOptionChart && isBasketStrategyBasketId(o.basketId)) return false
                return true
            }) ?? []
        }
        if(mode == 'EXPRESSION' && security &&( viewKey == ChartViewKey.STRATEGY || viewKey == ChartViewKey.STRATEGY_CHART)) {
            result = createBasketSLTPOrders
        }
        return result
    }, [mode, ordersHistory, securityList, createBasketSLTPOrders, basketId, viewKey, isIndividualOptionChart])

    const createBasketCollectivePosition = useMemo(() => (positions: Position[]) => {
        const basketTradeOrders = (tradeOrders || []).filter(order => order.selected)
        const hasIncomingOrders = basketTradeOrders.length > 0

        if (basketPositions.length === 0) {
            return undefined
        }

        let collectiveSecurity: Security | undefined = undefined;

        if (mode === 'EXPRESSION' && securityList?.[0]) {
            const first = resolvedSecuritiesMapRef.current.get(securityList[0])
            if (first?.underlyingSecurityCode) {
                collectiveSecurity = resolvedSecuritiesMapRef.current.get(getSecurityKey(first.exchange, first.underlyingSecurityCode))
            }
        }

        if (!collectiveSecurity) {
            if (basketPositions.length > 0) {
                collectiveSecurity = basketPositions[0].security;
            } else if (basketTradeOrders.length > 0 && basketTradeOrders[0].security) {
                collectiveSecurity = basketTradeOrders[0].security;
            } else {
                return undefined;
            }
        }

        const securityToOrderMap: Record<string, typeof basketTradeOrders> = {};
        basketTradeOrders.forEach(order => {
            if (!securityToOrderMap[order.security.code]) {
                securityToOrderMap[order.security.code] = [];
            }
            securityToOrderMap[order.security.code].push(order);
        });

        const processedSecurities = new Set<string>();

        let closedValue = 0
        let quantities: number[] = []
        let totalRealizedProfit = 0

        basketPositions.forEach(pos => {
            processedSecurities.add(pos.security.code);
            const orders = securityToOrderMap[pos.security.code] || [];

            let positionValue = pos.closePositionValue
            let positionQuantity = pos.closePosition

            // Add existing realized profit from position
            totalRealizedProfit += pos.realisedProfit || 0

            if (orders.length === 1) {
                const order = orders[0];
                const direction = order.transactionType === TransactionType.BUY ? 1 : -1;

                if (Math.sign(positionQuantity) !== Math.sign(direction)) {
                    // Squaring off position - calculate realized profit from square-off
                    const orderSqOffQuantity = Math.min(Math.abs(positionQuantity), Math.abs(order.quantity));
                    const originalAvgPrice = pos.closePositionValue / pos.closePosition;
                    const positionDirection = pos.closePosition > 0 ? 1 : -1;

                    // Add realized profit from square-off
                    totalRealizedProfit += positionDirection * orderSqOffQuantity * (order.price - originalAvgPrice);

                    const newQuantity = positionQuantity + direction * order.quantity;

                    if (orderSqOffQuantity < order.quantity) {
                        positionValue = (order.price ?? 0) * newQuantity
                        positionQuantity = newQuantity
                    } else {
                        positionValue = (pos.closePositionValue / pos.closePosition) * newQuantity
                        positionQuantity = newQuantity
                    }
                } else {
                    const orderValue = (order.price ?? 0) * order.quantity * direction
                    positionValue += orderValue
                    positionQuantity += order.quantity * direction
                }
            }
            closedValue += positionValue
            if (positionQuantity !== 0) {
                quantities.push(Math.abs(positionQuantity))
            }
        })

        Object.keys(securityToOrderMap).forEach(securityCode => {
            if (!processedSecurities.has(securityCode)) {
                const orders = securityToOrderMap[securityCode];
                orders.forEach(order => {
                    const direction = order.transactionType === TransactionType.BUY ? 1 : -1;
                    const orderValue = (order.price ?? 0) * order.quantity * direction
                    const orderQuantity = order.quantity * direction

                    closedValue += orderValue
                    quantities.push(Math.abs(orderQuantity))
                })
            }
        })

        const gcdValue = quantities.length > 0 ? calculateGcd(quantities) : 1
        const closeQuantity = closedValue >= 0 ? gcdValue : -gcdValue

        const collectivePosition: Position & { isBasket?: boolean, hasIncomingOrders?: boolean } = {
            basketId: basketId,
            product: basketPositions.length > 0 ? basketPositions[0].product : 0,
            security: collectiveSecurity!,
            strategyGroup: basketPositions.length > 0 ? basketPositions[0].strategyGroup : undefined,
            date: basketPositions.length > 0 ? basketPositions[0].date : 0,
            overnightPosition:0,
            overnightPositionValue: 0,
            closePosition: closeQuantity,
            closePositionValue: closedValue,
            dayBuyQuantity: 0,
            daySellQuantity: 0,
            dayBuyValue: 0,
            daySellValue: 0,
            realisedQuantity: 0,
            realisedBuyValue: 0,
            realisedSellValue: 0,
            realisedProfit: totalRealizedProfit / (collectiveSecurity?.priceMultiplier ?? 100),
            status: basketPositions.length > 0 ? basketPositions[0].status : Position_Status.ACTIVE,
            adjusted: basketPositions.some(p => p.adjusted),
            isBasket: true,
            hasIncomingOrders: hasIncomingOrders
        }
        return collectivePosition
    },[basketId, mode, securityList, basketPositions, tradeOrders])

    const computeChartPositions = useCallback((positionsList: Position[]) => {
        let result: {
            position: Position & {
                isBasket?: boolean,
                realizedProfit?: number
            }
            stopLossOrder: Order | null
            targetOrder: Order | null
        }[] = []
        const security = securityList?.[0]
        if (mode !== 'EXPRESSION' && security) {
            const stockPositions = positionsList.filter(d => {
                const key = getSecurityKey(d.security?.exchange ?? 0, d.security?.code ?? '')
                if (d.status !== Position_Status.ACTIVE || key !== security || !d?.closePosition) return false
                if (isIndividualOptionChart && isBasketStrategyBasketId(d.basketId)) return false
                return true
            })
            result = stockPositions.map(p => ({
                position: p,
                stopLossOrder: null,
                targetOrder: null
            }))
        }
        if(mode == 'EXPRESSION' && security && (viewKey == ChartViewKey.STRATEGY || viewKey == ChartViewKey.STRATEGY_CHART)){
            const position = createBasketCollectivePosition(positionsList)
            if(!position) return []
            result = [{
                position: position,
                stopLossOrder: null,
                targetOrder: null
            }]
        }
        return result
    }, [mode, securityList, createBasketCollectivePosition, viewKey, isIndividualOptionChart])

    const openOrdersRef = useRef(openOrders)
    openOrdersRef.current = openOrders
    const buildOpenOrders = useCallback(() => openOrdersRef.current, [])

    useChartOrderDrawingSync({
        enabled: !isOverviewChart,
        chartRef: chart,
        chartContext,
        openOrders,
        positions,
        buildChartPositions: computeChartPositions,
        buildOpenOrders,
    })

    const updateLoadingState = useCallback((loading: boolean) => {
        setLoading(loading);
        if (loading) {
            setChartEmpty(false);
        }
        callNative('setLoading', loading.toString());
    }, [])

    useEffect(() => {
        previousViewPortCandleInfo.current = null
        if (timeBucket && !screenerChart) {
            const timeBucketFeature = getTimeBucketFeatureName(timeBucket.timeSeries)
            const access = hasAccess(FeatureCategory.CHART, FeatureSubCategory.TIMEBUCKET, timeBucketFeature)
            setShowRestrictionOverlay(!access)
        } else {
            setShowRestrictionOverlay(false)
        }
    }, [timeBucket?.timeSeries, timeBucket?.timeValue, hasAccess, screenerChart, viewKey])
    const previousBucket = useRef<SuperChartConfiguration_TimeBucket | null>(null);
    useEffect(() => {
        if (previousBucket.current && isEqual(previousBucket.current, timeBucket)) return
        callNative('onTimeBucketUpdate', viewKey, JSON.stringify(timeBucket));
        previousBucket.current = cloneDeep(timeBucket);
    }, [timeBucket, viewKey])

    const timeRangeChangeListener = useCallback(
        (viewportCandleInfo: ViewportCandleInfo) => {
            setGotoDate(undefined)
            if (!prevViewportCandleInfoRef.current || !areTwoRangesAlmostSame(prevViewportCandleInfoRef.current, viewportCandleInfo, chart.current?.chartApi.timeScale()!!)) {
                onTimeRangeChange && onTimeRangeChange(viewportCandleInfo, chart.current?.notifyGoToDateToChart ? chart.current?.gotoDate : undefined)
                prevViewportCandleInfoRef.current = viewportCandleInfo
            }
        },
        [onTimeRangeChange]
    )

    const initializeStartListener = useCallback(() => {
        setChartInitialized(false)
    }, [])

    const initializeChartListener = useCallback(() => {
        setHasCompletedInitialLoad(true)
        setChartInitialized(true)
        if (chart.current) {
            const primaryData = chart.current.getPrimaryData()
            const isEmpty = !primaryData || primaryData.length === 0
            chartEmptyRef.current = isEmpty
            setChartEmpty(isEmpty)
            chart.current.setCrosshairHiddenForEmptyData(isEmpty)
        }
    }, [])

    useEffect(() => {
        if (!chart.current || !chartInitialized || !Object.keys(futureCompanyEvents).length) return
        const currentlySelectedSecurityCompanyCode = chart.current.securities[0]?.companyCode
        if (!currentlySelectedSecurityCompanyCode) return
        const selectedSecurityFutureEvents = futureCompanyEvents[currentlySelectedSecurityCompanyCode]
        if (!selectedSecurityFutureEvents || selectedSecurityFutureEvents.length === 0) return
        chart.current.updateFutureCompanyEvents(selectedSecurityFutureEvents)
    }, [futureCompanyEvents, chartInitialized])

    useEffect(() => {
        chartActiveRef.current = chartActive
    }, [chartActive])

    useEffect(() => {
        chartContextRef.current = chartContext
    }, [chartContext])

    useEffect(() => {
        // Ensure chart has full config (timeBucket, panes, etc.). Partial config from updateAtmOptionChart
        // or updateFutureChart can create config without timeBucket, causing black screen / no candles.
        const needsFullConfig = !chartConfig || !chartConfig.timeBucket;
        if (needsFullConfig) {
            dispatch(
                updateChartConfig({
                    viewKey,
                    config: { ...getDefaultChartConfig(initialConfig), ...chartConfig }
                })
            )
        } else {
            if (chart.current) {
                const chartInstanceConfig = chart.current.chartConfig
                const reduxConfig = chartConfig
                const settingsPatch = {
                    tradingSettings: {
                        ...defaultTradingSettings,
                        ...(reduxConfig?.tradingSettings ?? {})
                    },
                    alertSettings: reduxConfig?.alertSettings,
                    eventsSettings: reduxConfig?.eventsSettings,
                    scaleOptions: reduxConfig?.scaleOptions,
                    scalesAndLinesSettings: reduxConfig?.scalesAndLinesSettings,
                    canvasSettings: reduxConfig?.canvasSettings,
                    hideGrid: reduxConfig?.hideGrid,
                    overlayPriceScale: reduxConfig?.overlayPriceScale,
                    scaleInvertOption: reduxConfig?.scaleInvertOption,
                }
                if (!settingsPatch.tradingSettings?.showOrderMarkersOnChart || !settingsPatch.tradingSettings?.showTradeFromChart) {
                    settingsPatch.tradingSettings = {
                        ...defaultTradingSettings,
                        ...(reduxConfig?.tradingSettings ?? {})
                    }
                }
                const rawPdc = selectSnapshot(primarySecurityKey)(store.getState())?.previousClose
                const pdcMultiplier = resolvedSecurities[0]?.priceMultiplier || 100
                chart.current.prevDayClose = rawPdc ? rawPdc / pdcMultiplier : null
                chart.current.updateChartConfigSettings({
                    ...chartInstanceConfig,
                    ...settingsPatch,
                })
            }
        }
    }, [chartConfig])

    useEffect(() => {
        if (screenerChart || !timeBucketOverride || !chartConfig?.timeBucket) return
        if (isEqual(chartConfig.timeBucket, timeBucketOverride)) return
        dispatch(updateChartTimeBucket({ viewKey, timeBucket: timeBucketOverride }))
    }, [screenerChart, timeBucketOverride, chartConfig?.timeBucket, chartConfig, dispatch, viewKey])

    /** Embedded chart: `initialConfig.chartType` comes from URL; Redux keeps the previous type until synced here (unlike first load where `updateChartConfig` merges `initialConfig`). */
    useEffect(() => {
        if (!isEmbeded || screenerChart) return
        const urlChartType = initialConfig?.chartType
        if (urlChartType === undefined || chartConfig?.chartType === undefined) return
        if (chartConfig.chartType === urlChartType) return
        dispatch(updateChartType({ viewKey, chartType: urlChartType }))
    }, [isEmbeded, screenerChart, initialConfig?.chartType, chartConfig?.chartType, dispatch, viewKey])

    const renderChart = useMemo(() => {
        return container !== undefined && securityList.length > 0 && !!chartConfig && securityList.length === resolvedSecurities.length
    }, [container, securityList, chartConfig, resolvedSecurities.length])

    const chartPrerequisitesReady = !!(indicators?.length) && !!ws && !!calendarData?.holidays?.length && !!timeBuckets.length

    const showInitialChartChrome = useMemo(() => {
        if (hasCompletedInitialLoad) return false
        if (!renderChart || chartError || showRestrictionOverlay) return false
        return true
    }, [hasCompletedInitialLoad, renderChart, chartError, showRestrictionOverlay])

    const showMetadataPlaceholder = useMemo(() => {
        return (
            showInitialChartChrome &&
            showMetadata &&
            primary &&
            !screenerChart &&
            viewKey !== ChartViewKey.OVERVIEW
        )
    }, [showInitialChartChrome, showMetadata, primary, screenerChart, viewKey])

    const restrictScroll = useMemo(() => (screenerChart || disableScroll), [screenerChart, disableScroll])

    const showLeftPriceScale = chartConfig?.scaleOptions?.leftScaleVisible ?? false
    const showRightPriceScale = chartConfig?.scaleOptions?.rightScaleVisible ?? true
    const priceScaleOnLeft = showLeftPriceScale && !showRightPriceScale

    const activePriceScaleWidth = useMemo(() => {
        if (!chart.current) return 52
        const width = priceScaleOnLeft
            ? chart.current.getLeftScaleWidth()
            : chart.current.getRightScaleWidth()
        return width > 0 ? width : 52
    }, [priceScaleOnLeft, configUpdateVersion, chartInitialized])

    const activeTimeScaleHeight = useMemo(() => {
        if (!chart.current) return 28
        const height = chart.current.getTimeScaleHeight()
        return Number.isFinite(height) && height > 0 ? height : 28
    }, [configUpdateVersion, chartInitialized])

    // Price scale width is 0/stale until lightweight-charts finishes layout after moving the scale.
    useLayoutEffect(() => {
        if (!chartInitialized || !chart.current) return
        let raf2 = 0
        const bumpScaleWidth = () => setConfigUpdateVersion((v) => v + 1)
        const raf1 = requestAnimationFrame(() => {
            raf2 = requestAnimationFrame(bumpScaleWidth)
        })
        const timeoutId = window.setTimeout(bumpScaleWidth, 150)
        return () => {
            cancelAnimationFrame(raf1)
            if (raf2) cancelAnimationFrame(raf2)
            clearTimeout(timeoutId)
        }
    }, [priceScaleOnLeft, chartInitialized])

    const layoutOptions: DeepPartial<TimeChartOptions> = useMemo(() => {
        if (!chartConfig || !theme) return {}
        const hidePriceScales =
            isEmbeded && (restrictScroll || !!chartConfig.overlayPriceScale)
        const canvasSettings = mergeCanvasSettings(chartConfig.canvasSettings)

        const crosshairLineStyle = canvasLineStyleToLineStyle(canvasSettings.crosshairLineStyle)
        const crosshairColor = (() => {
            const c = tinycolor(canvasSettings.crosshairColor)
            c.setAlpha(canvasSettings.crosshairColorOpacity)
            return c.toRgbString()
        })()
        const crosshairWidth = canvasSettings.crosshairThickness as LineWidth
        const scaleLinesColor = canvasSettings?.scaleLinesColor
            ? (() => {
                const c = tinycolor(canvasSettings.scaleLinesColor)
                c.setAlpha(canvasSettings.scaleLinesColorOpacity)
                return c.toRgbString()
            })()
            : theme.palette.divider
        const config: DeepPartial<TimeChartOptions> = {
            ...chartLayoutOptions,
            layout: {
                panes: {
                    // separators between the panes are drawn with the color of the scale lines
                    separatorColor: scaleLinesColor
                },
                background: {
                    type: ColorType.Solid,
                    color: theme.palette.background.paper
                },
                fontFamily: `'Work Sans', sans-serif`,
                textColor: canvasSettings?.scaleTextColor
                    ? (() => {
                        const c = tinycolor(canvasSettings.scaleTextColor)
                        c.setAlpha(canvasSettings.scaleTextColorOpacity ?? 1)
                        return c.toRgbString()
                    })()
                    : theme.palette.text.secondary,
                fontSize: canvasSettings?.scaleTextFontSize ?? 12,
            },
            grid: {
                horzLines: {
                    visible: screenerChart || chartConfig.hideGrid ? false : (
                        canvasSettings.gridVisibility === 'both' ||
                        canvasSettings.gridVisibility === 'horizontal'
                    ),
                    color: canvasSettings?.gridHorzColor
                        ? (() => {
                            const c = tinycolor(canvasSettings.gridHorzColor);
                            c.setAlpha(canvasSettings.gridHorzColorOpacity ?? 0.08);
                            return c.toRgbString();
                        })()
                        : '#88888815'
                },
                vertLines: {
                    visible: screenerChart || chartConfig.hideGrid ? false : (
                        canvasSettings.gridVisibility === 'both' ||
                        canvasSettings.gridVisibility === 'vertical'
                    ),
                    color: canvasSettings?.gridVertColor
                        ? (() => {
                            const c = tinycolor(canvasSettings.gridVertColor);
                            c.setAlpha(canvasSettings.gridVertColorOpacity ?? 0.08);
                            return c.toRgbString();
                        })()
                        : '#88888815'
                }
            },
            timeScale: {
                visible: !hideTimescale,
                borderColor: canvasSettings?.scaleLinesColor
                    ? (() => {
                        const c = tinycolor(canvasSettings.scaleLinesColor)
                        c.setAlpha(canvasSettings.scaleLinesColorOpacity)
                        return c.toRgbString()
                    })()
                    : theme.palette.divider,
                fixLeftEdge: restrictScroll,
                fixRightEdge: restrictScroll,
                lockVisibleTimeRangeOnResize: restrictScroll,
                shiftVisibleRangeOnNewBar: true,
                allowShiftVisibleRangeOnWhitespaceReplacement: true,
                // lightweight-charts renders the highest-weight visible tick marks
                // (hours / days / months) in bold by default; keep all labels regular.
                allowBoldLabels: false,
                borderVisible: hidePriceScales || chartConfig.hideGrid || screenerChart
                    ? false
                    : !!canvasSettings?.scaleLinesColor
            },
            rightPriceScale: {
                alignLabels: chartConfig.scalesAndLinesSettings?.alignLabels ?? true,
                borderColor: canvasSettings?.scaleLinesColor
                    ? (() => {
                        const c = tinycolor(canvasSettings.scaleLinesColor)
                        c.setAlpha(canvasSettings.scaleLinesColorOpacity)
                        return c.toRgbString()
                    })()
                    : theme.palette.divider,
                // For embedded charts with overlay, always hide right scale
                visible: hidePriceScales ? false : !hideRightScale && showRightPriceScale,
                mode: chartConfig.scaleOptions?.rightScaleMode ?? PriceScaleMode.Normal,
                percentageModeReferenceSourceIndex: chartConfig.scaleOptions?.percentageModeReferenceSourceIndex ?? 0,
                borderVisible: !!canvasSettings?.scaleLinesColor,
                scaleMargins: CHART_PRICE_SCALE_MARGINS
            },
            leftPriceScale: {
                alignLabels: chartConfig.scalesAndLinesSettings?.alignLabels ?? true,
                borderColor: canvasSettings?.scaleLinesColor
                    ? (() => {
                        const c = tinycolor(canvasSettings.scaleLinesColor)
                        c.setAlpha(canvasSettings.scaleLinesColorOpacity)
                        return c.toRgbString()
                    })()
                    : theme.palette.divider,
                // For embedded charts with overlay, hide left scale (overlay handles it)
                visible: hidePriceScales ? false : hideLeftScale === true ? false : showLeftPriceScale,
                mode: chartConfig.scaleOptions?.leftScaleMode ?? PriceScaleMode.Normal,
                percentageModeReferenceSourceIndex: chartConfig.scaleOptions?.percentageModeReferenceSourceIndex ?? 0,
                borderVisible: !!canvasSettings?.scaleLinesColor,
                scaleMargins: CHART_PRICE_SCALE_MARGINS
            },
            crosshair: inlineCrosshair
                ? {
                    // Vertical long-dashed line only; values live in the inline card, not on the scales.
                    mode: CrosshairMode.Magnet,
                    horzLine: { visible: false, labelVisible: false },
                    vertLine: {
                        visible: true,
                        labelVisible: false,
                        color: crosshairColor,
                        width: 1,
                        style: LineStyle.LargeDashed,
                    },
                }
                : {
                    mode: restrictScroll ? CrosshairMode.Magnet : CrosshairMode.Normal,
                    horzLine: {
                        color: crosshairColor,
                        width: crosshairWidth,
                        style: crosshairLineStyle,
                    },
                    vertLine: {
                        color: crosshairColor,
                        width: crosshairWidth,
                        style: crosshairLineStyle,
                    },
                },
            // Long-press shows the crosshair; lifting the finger hides it again.
            ...(inlineCrosshair ? { trackingMode: { exitMode: TrackingModeExitMode.OnTouchEnd } } : {}),
            handleScale: !restrictScroll,
            handleScroll: !restrictScroll
                ? {
                    mouseWheel: true,
                    pressedMouseMove: true,
                    horzTouchDrag: true,
                    vertTouchDrag: true
                }
                : false
        }
        return config;
    }, [restrictScroll, theme, chartConfig, hideLeftScale, hideTimescale, hideRightScale, isEmbeded, showLeftPriceScale, showRightPriceScale, screenerChart, inlineCrosshair])

    const handleContextMenu = useCallback((e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        if (!!disableRealtimeUpdates) return
        e.preventDefault()
        const chartInstance = chart.current
        const paneElements = getPaneElements(chartInstance?.chartApi) ?? []
        const paneIndex = paneElements.findIndex((paneEl) => {
            const rect = paneEl.getBoundingClientRect()
            return e.clientY >= rect.top && e.clientY <= rect.bottom
        })
        const resolvedPaneIndex = paneIndex >= 0 ? paneIndex : 0
        const paneRect = paneElements[resolvedPaneIndex]?.getBoundingClientRect()
        const y = paneRect ? e.clientY - paneRect.top : e.nativeEvent.offsetY
        const ctx = chartInstance?.getContextMenuSeriesContext(resolvedPaneIndex)
        let price = ''
        if (ctx?.series) {
            price = ctx.series.coordinateToPrice(y)?.toFixed(2) ?? ''
        }
        contextMenuHostRef.current?.open({
            x: e.clientX,
            y: e.clientY,
            price,
            position: 'left',
            seriesGroup: ctx?.seriesGroupConfig ?? chartInstance?.primarySeriesGroup?.config
        })
    }, [disableRealtimeUpdates])

    const resetPanesConfig = async () => {
        let primaryPane: PaneConfig,
            seriesGroups: Record<string, SeriesGroup> = {},
            seriesObject: Record<string, Series> = {}
        const primarySec = mode !== 'EXPRESSION' ? resolvedSecurities[0] : undefined
        if (mode !== 'EXPRESSION' && securityList[0] && !primarySec) return

        if (!chartPanes || !chartPanes.length) {
            const conf = await getDefaultChartPanesConfig(
                chartType,
                mode === 'EXPRESSION',
                expression || '',
                securityList,
                comparableSecurities,
                viewKey as ChartViewKey,
                expression ? expressionToName.get(expression) || '' : '',
                disableVolumeOnLoad,
                primaryScaleId.toUpperCase() as Scale,
                primarySec
            )
            primaryPane = conf.primaryPane
            seriesGroups = conf.seriesGroups
            seriesObject = conf.seriesObject

            if (syncSettings.time) {
                // find the chart with the same timeBucket and set the gotoDate to the center of the viewport
                for (let key in widgets) {
                    const wdgt = widgets[key]
                    const chart = appContext.getChart(wdgt.settings.chartId)
                    if (
                        chart &&
                        wdgt.settings.chartId !== viewKey &&
                        chart.timeBucket.timeSeries === chartConfig.timeBucket.timeSeries &&
                        chart.timeBucket.timeValue === chartConfig.timeBucket.timeValue &&
                        chart.viewportCandleInfo.viewportCandles > 0
                    ) {
                        const data = chart.getRenderedPrimaryData()
                        const { barsAfter, barsBefore, viewportCandles, rangeStart, rangeEnd } = chart.viewportCandleInfo
                        const centerIndex = rangeStart + Math.ceil(viewportCandles / 2)
                        if (data[centerIndex]) {
                            const gotoDate = data[centerIndex].time
                            gotoDateRef.current = gotoDate
                            viewportCandleInfoRef.current = { ...chart.viewportCandleInfo }
                        }
                    }
                }
            }

            dispatch(
                updatePanesConfig({
                    viewKey: viewKey,
                    panesConfig: [primaryPane],
                    series: Object.values(seriesObject),
                    seriesGroups: Object.values(seriesGroups)
                })
            )
        } else {
            const {
                panes: newPanes,
                seriesGroups: newSeriesGroups,
                series: newSeries,
                panesConfig: newPanesConfig
            } = getNewPanesConfig(chartPanes, chartPanesConfig, chartSeriesGroups, chartSeries, false)
            const displayName =
                mode === 'EXPRESSION' ? (expressionToName.get(expression!) ?? convertExpressionToDisplayExpression(expression!)) : primarySec!.symbol
            newPanes.forEach((id) => {
                const paneConfig = newPanesConfig[id]
                if (paneConfig?.primary) {
                    paneConfig.seriesGroups.forEach((sgid) => {
                        let grp = {
                            ...newSeriesGroups[sgid]
                        }

                        if (grp?.primary) {
                            grp.name = displayName || ''
                            const primarySeries = { ...newSeries[grp.series[0]] }
                            const blankSeries = { ...newSeries[grp.series[1]] }
                            primarySeries.chartType = chartType
                            // Blank series must stay candlestick: baseline/area renderers use it for coordinateToPrice().
                            blankSeries.chartType = ChartType.CANDLESTICK
                            primarySeries.name = displayName || ''
                            blankSeries.name = ''
                            newSeries[primarySeries.id] = primarySeries
                            newSeries[blankSeries.id] = blankSeries
                            grp.series = [primarySeries.id, blankSeries.id]
                        }
                        newSeriesGroups[sgid] = grp
                    })
                }
            })
            dispatch(
                updatePanesConfig({
                    viewKey: viewKey,
                    panesConfig: Object.values(newPanesConfig),
                    series: Object.values(newSeries),
                    seriesGroups: Object.values(newSeriesGroups)
                })
            )
        }
    }

    useEffect(() => {
        if (renderChart && !mounted.current && chartConfig && widgets && syncSettings) {
            mounted.current = true
            void resetPanesConfig()
        }
    }, [renderChart, chartConfig, widgets, syncSettings])

    useEffect(() => {
        return () => {
            if (replayState?.viewKey === viewKey) {
                appContext.replayManager?.reset()
                dispatch(disableReplayMode())
            }
        }
    }, [viewKey, replayState?.viewKey, dispatch, appContext.replayManager])

    const updatePrimaryConfig = useCallback(debounce(100, (securities: Security[], expression: string, expressionName: string, timeBucket: SuperChartConfiguration_TimeBucket) => {
        if (!chart.current) return
        chart.current.updatePrimaryConfig({
            securities,
            expression,
            expressionName,
            timeBucket
        })
    }), [])

    useEffect(() => {
        if (!chart.current) return
        const securitiesList = resolvedSecurities.length === securityList.length ? resolvedSecurities : []
        if (securitiesList.length !== securityList.length) return

        const currentConfig = chartConfigRef.current
        if (
            currentConfig &&
            isEqual(chart.current.securities, securitiesList) &&
            chart.current.expression === (expression || '') &&
            chart.current.expressionName === expressionName &&
            isEqual(currentConfig.timeBucket, timeBucket)
        ) {
            return
        }

        updatePrimaryConfig(securitiesList, expression, expressionName, timeBucket)
    }, [updatePrimaryConfig, securityList, resolvedSecurities, expression, timeBucket, chartType, expressionName])

    useEffect(() => {
        chart.current && chart.current.updateExpressionToName(expressionToName)
    }, [expressionToName])

    useEffect(() => {
        if (chart.current) {
            chart.current.updateChartType(chartType)
        }
    }, [chartType])

    /** Must run after `updateChartType` so the new primary series receives `lastPriceAnimation` (custom baseline replaces the series instance on type change). */
    useEffect(() => {
        if (!chart.current || !chartInitialized) return
        const enable =
            isLiveTradingWindow &&
            !isReplayModeOn &&
            !chart.current.replayMode &&
            !chartConfig?.replayMode
        chart.current.updatePrimaryLastPriceAnimation(enable)
    }, [
        isLiveTradingWindow,
        isReplayModeOn,
        chartInitialized,
        chartType,
        chartConfig?.replayMode,
        viewKey
    ])

    /** Show the bar-close countdown only while the market is in a live trading window. */
    useEffect(() => {
        if (!chart.current || !chartInitialized) return
        const enable = isLiveTradingWindow && !isReplayModeOn && !chart.current.replayMode && !chartConfig?.replayMode
        chart.current.updateCountdownLiveTradingWindow(enable)
    }, [
        isLiveTradingWindow,
        isReplayModeOn,
        chartInitialized,
        chartType,
        chartConfig?.replayMode,
        viewKey
    ])

    useEffect(() => {
        if (chart.current) {
            chart.current.updateTouchEnabled(isTouchEnabled)
        }
    }, [isTouchEnabled])

    useEffect(() => {
        if (disableRealtimeUpdates || !chart.current || !chartInitialized) return
        chart.current.dataManager.enableRealtimeUpdates()
    }, [disableRealtimeUpdates, chartInitialized])

    useEffect(() => {
        if (isOverviewChart || !resolvedSecurities[0]) return
        const snap = selectSnapshot(primarySecurityKey)(store.getState())
        if (snap?.previousClose) return
        const security = resolvedSecurities[0]
        const timer = window.setTimeout(() => {
            fetchSecuritySnapshotsBulk({
                securities: [{ exchange: security.exchange, code: security.code }],
                dispatch
            }).catch(() => {/* snapshot unavailable — prev day close line won't be shown */})
        }, 150)
        return () => clearTimeout(timer)
    }, [isOverviewChart, primarySecurityKey, dispatch, resolvedSecurities])

    useEffect(() => {
        if (isOverviewChart || !chart.current || !chartInitialized || !resolvedSecurities[0]) return
        let prevClose: number | undefined
        const syncPrevDayClose = () => {
            const raw = selectSnapshot(primarySecurityKey)(store.getState())?.previousClose
            if (raw === prevClose) return
            prevClose = raw
            const multiplier = resolvedSecurities[0]?.priceMultiplier || 100
            chart.current!.prevDayClose = raw ? raw / multiplier : null
            chart.current!.updatePrevDayCloseLine()
        }
        syncPrevDayClose()
        return store.subscribe(syncPrevDayClose)
    }, [isOverviewChart, primarySecurityKey, chartInitialized, resolvedSecurities])

    useEffect(() => {
        if (!chart.current || !screenerChart || !chartInitialized || !selectedDateRangeScreener) return
        const dr = selectedDateRangeScreener
        if (
            chart.current.dateRange?.code === dr.code &&
            chart.current.dateRange?.timeBucketCode === dr.timeBucketCode &&
            chart.current.dateRange?.startTime === dr.startTime
        ) {
            return
        }
        chart.current.setSelectedDateRange(dr, false)
    }, [selectedDateRangeScreener, screenerChart, chartInitialized])

    /** Chart copies `timeBuckets` at construction; keep it current so setSelectedDateRange can resolve buckets added later (e.g. embedded custom buckets). */
    useEffect(() => {
        if (!chart.current) return
        chart.current.timeBuckets = timeBuckets
    }, [timeBuckets])

    /** Embedded chart (and similar) pass date range via props/URL; unlike screener, it was only applied at Chart construction, so in-app range changes never refetched. */
    useEffect(() => {
        // Gate on the first load only: chartInitialized drops to false while every reload is processed, and
        // skipping a range click in that window routed the bucket change through updatePrimaryConfig instead,
        // whose epoch bump aborted the in-flight load before it cleared dataLoading (chart stuck).
        if (!chart.current || screenerChart || !hasCompletedInitialLoad || !dateRangeForChart) return
        const dr = dateRangeForChart
        if (
            chart.current.dateRange?.code === dr.code &&
            chart.current.dateRange?.timeBucketCode === dr.timeBucketCode &&
            chart.current.dateRange?.startTime === dr.startTime
        ) {
            return
        }
        // Pass the resolved override as a fallback: when the URL bucket is custom, the store list may not
        // include it yet on this commit, and silently bailing here left the previous range window in place.
        chart.current.setSelectedDateRange(dr, false, false, timeBucketOverride)
    }, [dateRangeForChart, screenerChart, hasCompletedInitialLoad, timeBucketOverride])

    useEffect(() => {
        chart.current && chart.current.updateLayoutOptions(layoutOptions)
    }, [layoutOptions])

    const panesConfigKey = chartConfig?.panes?.length ?? 0
    const chartPanesKey = chartPanes.join(',')

    useLayoutEffect(() => {
        if (!chart.current || !chartInitialized) return
        const chartApi = chart.current.chartApi
        if (!chartApi) return

        let cancelled = false
        let attempts = 0
        const maxAttempts = 40

        const apply = (paneElements: HTMLDivElement[]) => {
            setPaneContainers((prev) => {
                if (prev.length === paneElements.length && prev.every((p, i) => p === paneElements[i])) return prev
                return paneElements
            })
        }

        const sync = () => {
            if (cancelled) return
            const paneElements = Array.from(getPaneElements(chartApi) || []) as HTMLDivElement[]
            const expected = chartPanes.length
            if (expected > 0 && paneElements.length !== expected && attempts < maxAttempts) {
                attempts += 1
                requestAnimationFrame(sync)
                return
            }
            apply(paneElements)
        }

        sync()
        return () => {
            cancelled = true
        }
    }, [panesConfigKey, chartPanesKey, viewKey, chartInitialized, configUpdateVersion])

    useEffect(() => {
        if (chart.current && calendarData) {
            chart.current.updateTradingCalendarData(calendarData)
        }
    }, [calendarData])

    useEffect(() => {
        if (!comparableSecurities?.length || !chart.current) return
        chart.current.updateComparableSecurities(comparableSecurities, false, {
            comparableSecurityPanePlacements: chartConfig?.comparableSecurityPanePlacements,
            comparableSecurityPanePlacement: chartConfig?.comparableSecurityPanePlacement
        })
    }, [
        comparableSecurities,
        chartConfig?.comparableSecurityPanePlacements,
        chartConfig?.comparableSecurityPanePlacement
    ])

    useLayoutEffect(() => {
        if (toggleFocus) {
            dispatch(
                setActiveChart({
                    chartId: viewKey,
                    chartFocused: true
                })
            )
        }

        return () => {
            if (toggleFocus) {
                dispatch(
                    setActiveChart({
                        chartFocused: false
                    })
                )
            }
            unSubscribeSecurites()
            appContext.removeChart(viewKey)
            chartContextRef.current?.chart?.destroy()
            // The debug global otherwise pins the destroyed chart (and its DOM) for the life of the tab.
            //@ts-ignore
            if (window.chart === chart.current || window.chart === chartContextRef.current?.chart) window.chart = null
            chart.current = null
        }
    }, [])

    const getChartSnapshot = useCallback((): ChartAlertSnapshot => {
        const snapshot = chart.current?.getChartSnapshot(0) || {}
        const primaryData = chart.current?.getRenderedPrimaryData() as ChartCandle[]
        const [candle] = (primaryData || [])?.slice(-1)

        const chartSnapshot: ChartAlertSnapshot = {
            securityList: resolvedSecurities,
            comparableSecurities,
            availableTimeBuckets: timeBuckets.map((tb) => convertToTimeBucket(tb)),
            chartType,
            data: {
                time: candle?.time ?? 0 as Time,
                open: candle?.open ?? 0,
                high: candle?.high ?? 0,
                low: candle?.low ?? 0,
                close: candle?.close ?? 0,
                customValues: {
                    volume: candle?.volume ?? 0
                }
            },
            timeBucket: convertToTimeBucket(timeBucket),
            expressionMode: expression && expression.length ? true : false,
            expression: expression || '',
            indicators: []
        }

        chartPanes.forEach((pid) => {
            const paneConfig = chartPanesConfig[pid]
            if (!paneConfig) return

            paneConfig.seriesGroups.forEach((sgid) => {
                const grp = chartSeriesGroups[sgid]
                const indicatorGroup = chart.current?.seriesGroupRef[sgid] as IndicatorSeriesGroup | undefined
                if (!grp || !indicatorGroup) return

                const indicator = indicatorGroup.indicator
                if (!indicator) return

                // Patterns (and pivot points) are SERIES_DRAWING; only include pattern indicators for alerts.
                const isStandardIndicator = [
                    SeriesGroupCategory.SERIES_INDICATOR,
                    SeriesGroupCategory.SERIES_VOLUME,
                ].includes(grp.category)
                const isPatternDrawing =
                    grp.category === SeriesGroupCategory.SERIES_DRAWING && isPatternIndicator(indicator)
                if (!isStandardIndicator && !isPatternDrawing) return

                const { code, name, inputs, outputs, shortName } = indicator
                const values: Record<string, SingleValueData> = {}
                grp.series.forEach((sid) => {
                    const grpSnapshot = snapshot[sgid] || {}
                    const srs = chartSeries[sid]
                    const data = grpSnapshot[sid]
                    if (!data || !data.length || !srs) return
                    const [candle] = data.slice(-1)

                    if (candle) {
                        values[srs.name] = candle as SingleValueData<Time>
                    }
                })
                chartSnapshot.indicators.push({
                    id: grp.id,
                    code,
                    shortName,
                    displayName: getIndicatorDisplayName(
                        indicator,
                        grp.inputValues,
                        (value) => chart.current?.getIndicatorOutputSourceLabel(value) ?? value
                    ),
                    name,
                    config: grp.inputValues,
                    inputs,
                    outputs,
                    outputValues: values
                })
            })
        })

        return chartSnapshot
    }, [
        resolvedSecurities,
        comparableSecurities,
        timeBuckets,
        chartType,
        timeBucket,
        expression,
        chartPanes,
        chartPanesConfig,
        chartSeriesGroups,
        chartSeries
    ])

    const getAlertSnapshot = useCallback((): AlertData => {
        const alertSeriesGrp: AlertSeriesGrp[] = []
        const snapshot = chart.current?.getChartSnapshot(0) || {}
        chartPanes.forEach((pid) => {
            const paneConfig = chartPanesConfig[pid]
            if (!paneConfig) return

            paneConfig.seriesGroups.forEach((sgid) => {
                const grp = chartSeriesGroups[sgid]
                if (!grp) return
                const alertGrp: AlertSeriesGrp = {
                    id: sgid,
                    code: grp.scriptCode,
                    name: grp.name,
                    description: grp.description,
                    serieses: [],
                    primary: grp.primary
                }
                grp.series.forEach((sid) => {
                    const grpSnapshot = snapshot[sgid] || {}
                    const srs = chartSeries[sid]
                    const data = grpSnapshot[sid]
                    if (!data || !data.length || !srs) return
                    const [candle] = data.slice(-1)

                    if (candle) {
                        alertGrp.serieses.push({
                            id: sid,
                            name: srs.name,
                            data: candle
                        })
                    }
                })
                alertSeriesGrp.push(alertGrp)
            })
        })
        return {
            grps: alertSeriesGrp,
            securities: resolvedSecurities
        }
    }, [chartPanes, chartPanesConfig, chartSeries, chartSeriesGroups, resolvedSecurities])

    const publishesAlertSnapshots = primary || viewKey === ChartViewKey.STRATEGY_CHART

    useEffect(() => {
        if (!publishesAlertSnapshots) return
        appContext.setAlertSnapshotMethod(viewKey, getAlertSnapshot)
    }, [getAlertSnapshot, publishesAlertSnapshots])

    useEffect(() => {
        if (!publishesAlertSnapshots) return
        appContext.setChartSnapshotMethod(viewKey, getChartSnapshot)
    }, [getChartSnapshot, publishesAlertSnapshots])

    /** Hover fields live on chart + ref only — never push hover into React context (metadata toolbar re-renders). */
    const patchChartContextRef = useCallback((patch: Partial<ChartContextType>) => {
        if (!chartContextRef.current) return
        chartContextRef.current = { ...chartContextRef.current, ...patch }
    }, [])

    const onMouseEnter = useCallback(() => {
        setChartHoverActive(viewKey, true)
        patchChartContextRef({ hoverActive: true })
    }, [patchChartContextRef, viewKey])

    const onMouseLeave = useCallback(() => {
        setChartHoverActive(viewKey, false)
        patchChartContextRef({ hoverActive: false, hoveredPaneId: undefined })
    }, [patchChartContextRef, viewKey])

    /** Refresh the OHLC strip for the current crosshair (or revert to live values when cleared). */
    const publishCrosshairSnapshot = useCallback(
        (data?: CrossHairInfo) => {
            if (screenerChart || !chart.current) return
            if (data?.point) {
                const offset = chart.current.currentCrosshairOffset || 0
                setChartCrosshairSnapshotForView(viewKey, chart.current.getChartSnapshot(offset))
            } else {
                setChartCrosshairSnapshotForView(viewKey, null)
            }
            bumpChartCrosshairRevision(viewKey)
        },
        [screenerChart, viewKey]
    )

    const handleCrosshairMove = useCallback(
        (data?: CrossHairInfo) => {
            if (screenerChart && data?.point && data.time) {
                const renderedData = chart.current?.getRenderedPrimaryData()
                if (renderedData && renderedData.length > 0) {
                    const candleIndex = renderedData.findIndex((candle: any) => candle.time === data.time)
                    if (candleIndex !== -1) {
                        const candle = renderedData[candleIndex] as any
                        const closePrice = candle.close || data.price
                        const openPrice = candle.open
                        const isProfit = openPrice !== undefined ? closePrice >= openPrice : undefined
                        setScreenerTooltipData({
                            price: closePrice,
                            volume: candle.volume || candle.customValues?.volume,
                            isProfit,
                            time: data.time as number
                        })
                        setScreenerTooltipPosition({ x: data.point.x, y: data.point.y })
                        setScreenerTooltipVisible(true)
                    }
                }
            } else if (screenerChart) {
                setScreenerTooltipVisible(false)
            }

            publishCrosshairSnapshot(data)

            // Forward clears too, otherwise a synced crosshair lingers on sibling panes
            // after this pane's crosshair is dismissed (e.g. tap to exit touch tracking).
            onCrosshairMove && onCrosshairMove(data)
        },
        [onCrosshairMove, publishCrosshairSnapshot, screenerChart]
    )

    const unSubscribeSecurites = () => {
        const tag = chartContextRef.current?.chart.subscriptionTag ?? ''
        if (isEmbeded) {
            callNative('unsubscribeSecurityCandles', tag);
            return;
        }
        // Only this chart's tag: the reducer merges by tag. This runs from an unmount cleanup, so any other
        // entries would be the list as of this chart's first render and would revert or resurrect other tags.
        const payload: Partial<WebSocketRequestMessage_Subscription> = {
            candleSubscriptions: [
                {
                    tag,
                    securities: [],
                    timeBucket: { timeSeries: 1, value: 1 }
                }
            ]
        }
        websocketActions.setWebsocketSubscribeList(dispatch, payload)
    }

    const subscribeSecurities = (securities: WebSocketRequestMessage_Security[]) => {

        if (isEmbeded) {
            callNative('subscribeSecurityCandles', subscriptionTag, securities.map(v => getSecurityKey(v.exchange, v.code)).join(','));
            return;
        }
        const accessToken = localStorage.getItem(ACCESS_TOKEN)
        if (accessToken) {
            const payload: Partial<WebSocketRequestMessage_Subscription> = {
                candleSubscriptions: [
                    {
                        tag: subscriptionTag,
                        securities,
                        timeBucket: {
                            timeSeries: TimeBucket_TimeSeries.SECOND,
                            value: 1
                        }
                    }
                ]
            }
            websocketActions.setWebsocketSubscribeList(dispatch, payload)
        }
    }

    useEffect(() => {
        if (chartContext?.chart && chart.current) {
            chart.current.updateOrdersMarkersOnChartDrawing(chartOrders)
        }
    }, [chartOrders, chartContext?.chart])

    useEffect(() => {
        if (chartContext?.chart && chart.current) {
            chart.current.updateChartAlertsDrawing(chartAlertsOverlay)
        }
    }, [chartAlertsOverlay, chartContext?.chart])

    const removeGotoDateTimeout = useRef<number | undefined>(undefined)

    const effectiveDefaultPriceScaleId = useMemo(() => {
        const opts = chartConfig?.scaleOptions
        if (opts?.leftScaleVisible && !opts?.rightScaleVisible) return 'LEFT'
        if (!opts?.leftScaleVisible && opts?.rightScaleVisible) return 'RIGHT'
        const primary = Object.values<SeriesGroup>(chartConfig?.seriesGroups ?? {}).find((sg) => sg.primary)
        return (primary?.scale ?? primaryScaleId).toUpperCase()
    }, [chartConfig?.scaleOptions, chartConfig?.seriesGroups, primaryScaleId])

    useEffect(() => {
        if (!container || chart.current || !ws || !renderChart || !chartPanes?.length || !chartConfig || !calendarData || !timeBuckets.length) return
        if (screenerChart && !selectedDateRangeScreener) return

        const drawings = selectChartDrawings(viewKey)(store.getState())

        chart.current = new Chart({
            id: viewKey,
            defaultPriceScaleId: effectiveDefaultPriceScaleId,
            noWhitespace,
            noOffset,
            container,
            layoutOptions,
            ws,
            drawings,
            subscriptionTag,
            tradingCalendarData: calendarData,
            expressionToName,
            gotoDate: gotoDateRef.current,
            overlayPriceScale: chartConfig?.overlayPriceScale ?? false,
            isEmbedded: isEmbeded,
            restrictScroll: restrictScroll,
            viewportCandleInfo: viewportCandleInfoRef.current ? { ...viewportCandleInfoRef.current } : undefined,
            dateRange: screenerChart ? selectedDateRangeScreener : dateRangeForChart,
            chartConfig: {
                ...chartConfig,
                chartType: chartType
            },
            chartType,
            emptyTimeslotsSize: screenerChart ? 0 : 150,
            timeBuckets,
            comparableSecurities,
            disableRealtimeUpdates,
            expression,
            expressionName,
            theme,
            disableDrawings,
            screenerChart,
            touchEnabled: isTouchEnabled,
            historyManager: manager,
            persistViewport: persistViewport || false,
            allowTradeFromChart: (viewKey === ChartViewKey.OVERVIEW || viewKey.includes('screener-chart-')) ? false : true,
            onDataUpdate() {
                if (!chart.current || viewKey === ChartViewKey.OVERVIEW) return
                const snapshot = chart.current.getChartSnapshot(0)
                setChartSnapshotForView(viewKey, snapshot)
                if (chart.current.crossHairInfo) {
                    const offset = chart.current.currentCrosshairOffset || 0
                    setChartCrosshairSnapshotForView(viewKey, chart.current.getChartSnapshot(offset))
                    bumpChartCrosshairRevision(viewKey)
                }
                const primaryMeta = chart.current.getPrimarySeriesMetadata()
                const nextError = primaryMeta?.chartError ?? null
                if (chartErrorRef.current !== nextError) {
                    chartErrorRef.current = nextError
                    setChartError(nextError)
                }

                const primaryData = chart.current.getPrimaryData()
                const isEmpty = !primaryData || primaryData.length === 0
                if (chartEmptyRef.current !== isEmpty) {
                    chartEmptyRef.current = isEmpty
                    setChartEmpty(isEmpty)
                    chart.current.setCrosshairHiddenForEmptyData(isEmpty)
                }

                const primarySecurity = chart.current.securities?.[0]
                if (primarySecurity) {
                    const securityKey = getSecurityKey(primarySecurity.exchange, primarySecurity.code)
                    const lastCandle = chart.current.dataManager.data[securityKey]?.slice(-1)?.[0]
                    if (lastCandle) {
                        publishChartBidAsk(viewKey, lastCandle.topBid ?? 0, lastCandle.topAsk ?? 0)
                    }
                }
            },
            onMouseDown() {
                setGotoDate(undefined)
            },
            onInitializeStart: initializeStartListener,
            onInitialized: initializeChartListener,
            subscribeSecurities(securities: WebSocketRequestMessage_Security[], timeBucket: TimeBucket) {
                subscribeSecurities(securities)
            },
            onCrosshairMove: handleCrosshairMove,
            onSyncedCrosshairChange: publishCrosshairSnapshot,
            onGotoDateUpdated(gotoDate: number) {
                clearTimeout(removeGotoDateTimeout.current)
                setGotoDate(gotoDate)
                removeGotoDateTimeout.current = window.setTimeout(() => setGotoDate(undefined), 3000)
            },
            onTimeRangeChange: timeRangeChangeListener,
            onMouseEnter,
            onMouseLeave,
            onConfigUpdate(config: ChartConfig) {
                if (!chart.current) return
                const latestConfig = cloneDeep(chart.current.chartConfig)
                chartConfigRef.current = latestConfig
                if (viewKey === ChartViewKey.OVERVIEW) return
                chart.current &&
                    dispatch(
                        updateChartConfig({
                            viewKey,
                            config: latestConfig
                        })
                    )
                setConfigUpdateVersion(v => v + 1)
            },
            onDrawingsUpdate: debounce(100, (drawings) => {
                if (!chart.current) return
                chart.current &&
                    dispatch(
                        updateChartDrawings({
                            viewKey,
                            drawings: JSON.parse(JSON.stringify(drawings))
                        })
                    )
            }),
            drawingsEventListener(event, data, forced) {
                onDrawingEvent && onDrawingEvent(event, data, forced)
            },
            onViewportCandleInfoChange(candleInfo: ViewportCandleInfo) {
                if (candleInfo.barsAfter > 0) {
                    setGotoCurrentButtonVisible(true)
                } else {
                    setGotoCurrentButtonVisible(false)
                }
                onViewportCandleInfoChange && onViewportCandleInfoChange(candleInfo)
            },
            onTooltipDataChange: (show, title, x, y) => {
                if (freezeChartHoverReactState || isEmbeded) return
                setShowChartTooltip(show)
                setTooltipTitle(title)
                tooltipData.current = { x, y }
            },
            onPriceScaleLongPress: () => {
                if (isEmbeded) {
                    callNative('onPriceScaleLongPress')
                }
            },
            onSeriesGroupError: (seriesGroupId, error) => {
                setChartContext(prev => {
                    if (!prev) return null
                    const next = { ...(prev.seriesGroupErrors || {}) }
                    if (error) {
                        next[seriesGroupId] = error
                    } else {
                        delete next[seriesGroupId]
                    }
                    return { ...prev, seriesGroupErrors: next }
                })
            },
            onLoading: updateLoadingState,
            indicators: indicators ?? [],
            securities: resolvedSecurities,
            timeBucket,
            timezone
        })

        gotoDateRef.current = undefined
        viewportCandleInfoRef.current = null
        // primary && chart.current && appContext.setChart(viewKey, chart.current)
        chart.current && appContext.setChart(viewKey, chart.current)

        setChartContext({
            hoverActive: false,
            hoveredPaneId: undefined,
            viewKey,
            chart: chart.current,
            seriesGroupErrors: {}
        })

        //@ts-ignore
        window.chart = chart.current
    }, [indicators, renderChart, container, gotoCurrentButtonVisible, chartPanes, disableDrawings, chartConfig, calendarData, onTimeRangeChange, screenerChart, selectedDateRangeScreener, dateRangeForChart, timeBucket])


    useEffect(() => {
        if (chart.current) {
            chart.current.theme = theme
            const axisLabelColor = theme.palette.mode === 'dark' ? '#142e61' : '#e3eefd'
            chart.current.highPriceLine?.applyOptions({ axisLabelColor })
            chart.current.lowPriceLine?.applyOptions({ axisLabelColor })
        }
    }, [theme])

    useEffect(() => {
        if (chart.current) {
            chart.current.updateCrossHairMoveCallback(handleCrosshairMove)
        }
    }, [handleCrosshairMove])

    useEffect(() => {
        if (chart.current) {
            chart.current.updateTimeRangeChangeListener(timeRangeChangeListener)
        }
    }, [timeRangeChangeListener])

    const onGotoCurrentButtonClick = useCallback(() => {
        if (!chart.current) return
        if (chart.current.chartConfig?.replayMode) {
            chart.current.chartApi.timeScale().scrollToPosition(10, false)
            return
        }
        chart.current.gotoCurrentTime()
    }, [])

    const updateScaleMode = useCallback((scaleMode: PriceScaleMode) => {
        chart.current && chart.current.updatePriceScaleMode(scaleMode, false)
    }, [])

    const updateInvertScale = useCallback((invert: boolean) => {
        chart.current && chart.current.updateInvertScale(invert)
    }, [])

    const movePrimaryPriceScale = useCallback(() => {
        chart.current?.movePrimaryPriceScale()
    }, [])
    const chartContainerStyle: React.CSSProperties = {
        width: '100%',
        flex: '1 1 0',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column'
    }

    const disabledPriceScaleModes = useMemo(() => {
        if (!chartConfig) {
            return []
        }
        if (chartConfig?.comparableSecurities?.length > 1) {
            return [PriceScaleMode.Logarithmic, PriceScaleMode.Normal]
        } else if (chartConfig?.comparableSecurities?.length > 0) {
            return [PriceScaleMode.Logarithmic]
        } else {
            return []
        }
    }, [chartConfig])

    const onDrawingClick = useCallback(
        (drawingType: DrawingTypes, options: Record<string, any>, enter?: boolean) => {
            if (!chart.current) return
            if (enter) {
                chart.current.drawingManager.addDrawing(drawingType, DrawingDisplayType.DRAWING, options, {})
            } else {
                chart.current.drawingManager.abort()
            }
        },
        [chart]
    )
    const primaryPaneIndex = useMemo(
        () => chartPanes.findIndex((id) => panesConfig[id].primary),
        [chartPanes, panesConfig]
    )

    const chartMountOverlaysProps = useMemo((): ChartMountOverlaysProps => {
        if (freezeChartHoverReactState && !screenerChart) {
            return {
                screenerTooltipVisible: false,
                screenerTooltipPosition: { x: 0, y: 0 },
                screenerTooltipData: null
            }
        }
        const primaryPaneContainer = paneContainers?.[primaryPaneIndex]
        const renderedPrimaryData = chart.current?.getRenderedPrimaryData()
        const renderedTimestamps = renderedPrimaryData?.map((dt: ChartCandle) => dt?.time as number) ?? []
        const dataIndex = gotoDate ? binarySearch(renderedTimestamps, gotoDate) : -1
        const price = renderedPrimaryData?.[dataIndex]
            ? 'high' in renderedPrimaryData[dataIndex]
                ? (renderedPrimaryData[dataIndex] as ChartCandle).high
                : 'value' in renderedPrimaryData[dataIndex]
                    ? (renderedPrimaryData[dataIndex] as ChartCandle).series
                    : NaN
            : NaN
        const y = chart.current?.primarySeriesGroup?.getPrimarySeriesApi()?.priceToCoordinate(price as number)
        const x = gotoDate && chart.current ? chart.current.chartApi.timeScale().timeToCoordinate(gotoDate as Time) : undefined

        const gotoDateTooltip =
            gotoDate && primaryPaneContainer && x !== undefined && y !== undefined && y !== null
                ? {
                      gotoDate,
                      x: x as number,
                      top: primaryPaneContainer.offsetTop + y,
                  }
                : null

        const rootTooltip =
            showChartTooltip && primaryPaneContainer
                ? {
                      title: tooltipTitle,
                      x: tooltipData.current.x,
                      top: primaryPaneContainer.offsetTop + tooltipData.current.y - 5,
                  }
                : null

        const firstSecurity = chart.current?.securities?.[0]

        return {
            gotoDateTooltip,
            rootTooltip,
            screenerChart,
            screenerTooltipVisible,
            screenerTooltipPosition,
            screenerSecurity: firstSecurity
                ? {
                      symbol: firstSecurity.symbol || firstSecurity.code,
                      logoUrl: firstSecurity.logoUrl,
                      code: firstSecurity.code,
                  }
                : undefined,
            screenerTooltipData,
        }
    }, [
        freezeChartHoverReactState,
        gotoDate,
        chartPanes,
        paneContainers,
        panesConfig,
        primaryPaneIndex,
        showChartTooltip,
        tooltipTitle,
        screenerChart,
        screenerTooltipVisible,
        screenerTooltipPosition,
        screenerTooltipData,
    ])

    const gotoCurrentDateTooltipContent = useMemo(
        () => (
            <ScrollToRecentBarButton
                visible={gotoCurrentButtonVisible && !chartEmpty}
                onClick={onGotoCurrentButtonClick}
                priceScaleWidth={activePriceScaleWidth}
                timeScaleHeight={activeTimeScaleHeight}
            />
        ),
        [gotoCurrentButtonVisible, chartEmpty, onGotoCurrentButtonClick, activePriceScaleWidth, activeTimeScaleHeight]
    )

    return (
        <Box
            key={'chart-container'}
            className={cx(classes.chartContainer, 'font-feature-num')}
            sx={{
                border: 'none',
                borderRadius: 0,
                width: '100%',
                height: '100%',
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            {indicatorsDrawingsShortcuts}
            <div style={{ ...chartContainerStyle, position: 'relative' }}>
                {/* do not remove chart-selector class. used for finding chart container*/}
                <div
                    className={cx(
                        classes.chart,
                        'chart-selector',
                        replayState?.status === ReplayStatus.SELECTION &&
                            replayState.layoutId &&
                            layoutId !== replayState.layoutId &&
                            classes.replaySelectionMode
                    )}
                    ref={chartRef}
                    style={{ position: 'relative' }}
                >
                    {showRestrictionOverlay && timeBucket && (
                        <ChartRestrictionOverlay
                            viewKey={viewKey}
                            feature={timeBucket.name || 'This TimeBucket'}
                            currentTimeBucket={timeBucket}
                        />
                    )}
                    {!!chartError && (
                        <ChartErrorOverlay message={chartError} />
                    )}
                    {showInitialChartChrome && (
                        <Box className={classes.chartShimmerOverlay}>
                            <ChartSkeletonPlaceholder
                                hideTimescale={hideTimescale}
                                hideLeftScale={hideLeftScale}
                                hideRightScale={hideRightScale}
                                showLeftPriceScale={showLeftPriceScale}
                                showRightPriceScale={showRightPriceScale}
                                screenerChart={screenerChart}
                                hideGrid={chartConfig?.hideGrid}
                            />
                        </Box>
                    )}
                    {showMetadataPlaceholder && (
                        <Box className={classes.chartMetadataPlaceholder}>
                            <ChartMetadataBarPlaceholder />
                        </Box>
                    )}
                    {!chartError && !loading && chartInitialized && chartEmpty && (
                        <ChartEmptyStateOverlay
                            symbol={resolvedSecurities.length === 1 ? resolvedSecurities[0].symbol || resolvedSecurities[0].displayName || resolvedSecurities[0].code : undefined}
                        />
                    )}
                    <ChartMountSurface
                        viewKey={viewKey}
                        showRestrictionOverlay={showRestrictionOverlay}
                        isEmbedded={isEmbeded}
                        noData={chartEmpty}
                        onContextMenu={handleContextMenu}
                        onMount={handleChartMount}
                    />
                    {chartPrerequisitesReady && <ChartMountOverlays {...chartMountOverlaysProps} />}
                    {inlineCrosshair && chartInitialized && chart.current && (
                        <InlineCrosshairOverlay chart={chart.current} />
                    )}
                    {isTouchEnabled && !disableDrawings && chartInitialized && chart.current && (
                        <TouchDrawStepBanner chart={chart.current} />
                    )}
                    {!hideActions && (
                        <>
                            {gotoCurrentDateTooltipContent}
                            <ChartScaleMenu
                                viewKey={viewKey}
                                dimmed={isMultiPaneLayout && !chartActive}
                                updateScaleMode={updateScaleMode}
                                scaleOnLeft={priceScaleOnLeft}
                                scaleWidth={activePriceScaleWidth}
                                timeScaleHeight={activeTimeScaleHeight}
                                disabledPriceScaleModes={disabledPriceScaleModes}
                                updateInvertScale={updateInvertScale}
                                movePrimaryPriceScale={movePrimaryPriceScale}
                            />
                        </>
                    )}
                    {/* do not remove replay-backdrop-selector class. used for finding replay backdrop from within chart*/}
                    <div className={cx(classes.replayBackdrop, 'replay-backdrop-selector')}>
                        <ScissorIcon className={cx(classes.scissorIcon, 'scissor-selector')} />
                    </div>
                    {primary && isWidget && chartInitialized && !isReplayModeOn && !screenerChart && viewKey !== ChartViewKey.OVERVIEW && !isEmbeded && (
                        <ChartSettingsButtons viewKey={viewKey} allowSplit={allowSplit} security={resolvedSecurities[0]} />
                    )}
                </div>
                {primary && !hideActions && chartInitialized && (
                    <ChartContextMenuHost ref={contextMenuHostRef} viewKey={viewKey} />
                )}
                {chartContext && (
                    <ChartContext.Provider value={chartContext}>
                        {paneContainers && showMetadata && (
                            <>
                                <ChartMetadata
                                    viewKey={viewKey}
                                    paneElements={paneContainers}
                                    showLogo={showLogo}
                                    screenerChart={screenerChart}
                                    primary={primary}
                                    onSecuritySelected={onSecuritySelected}
                                    tradeOptions={tradeOptions}
                                />
                            </>
                        )}
                        {!hideChartFooter && !isEmbeded && (
                            <Paper className={classes.chartFooter} key="chart-footer">
                                <ChartFooter activeChartId={activeChartId} onDrawingClick={onDrawingClick} />
                            </Paper>
                        )}
                        {!screenerChart && viewKey !== ChartViewKey.OVERVIEW ? (
                            <ChartOverlays chartId={viewKey} />
                        ) : null}
                    </ChartContext.Provider>
                )}
            </div>
        </Box>
    )
})

ChartWrapper.displayName = 'ChartWrapper'

export default memo(ChartWrapper)
